import React, { useEffect, useRef, useState } from 'react';
import { Plus, Home, LogOut } from 'lucide-react';
import Cards from '../Components/Dashboard/Cards';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import ThemeChanger from '../Components/ThemeChanger'
import api from '../api';
import DashboardLoader from '../Components/Loaders/DashboardLoader';

export default function Dashboard() {
  const { auth, setAuth } = useAuth();
  const [roadmaps, setRoadMaps] = useState([]);
  const [loading, setLoading] = useState(null);
  const navigate = useNavigate();
  const [roadmapToDelete, setRoadmapToDelete] = useState(null);

  async function getRoadmap() {
    setLoading(true)
    api.get("/user/get-all-roadmaps").then(res => {
      const response = res.data;
      if (response.statusCode === 200) {
        setRoadMaps(response.data);
        setLoading(false)
      } else {
        navigate("/login")
      }

    });
  }

  useEffect(() => {
    if (!auth.id || !auth.role) {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      if (token) {
        sessionStorage.setItem('accessToken', token);
        navigate('/dashboard');
      } else {
        navigate('/login');
      }
      api.get("/auth/get-login-info").then(res => {
        const response = res.data;
        
        if (response.statusCode !== 200) {
          navigate("/login");
        } else {
          setAuth({
            id: response.data.id,
            role: response.data.role,
          })
          getRoadmap();
          return;
        }
      })
    }
    if (auth.id && auth.role) {
      getRoadmap()
    }
  }, [])

  const confirmDelete = (id) => {
    setRoadmapToDelete(id);
  };

  const handleDeleteConfirmed = () => {
    if (!roadmapToDelete) return;
    api.delete("/user/delete-roadmap?roadMapId=" + roadmapToDelete).then(res => {
      if (res.data.statusCode === 200) {
        setRoadMaps(prev => prev.filter(r => r._id !== roadmapToDelete));
        setRoadmapToDelete(null);
      }
    });
  };

  const handleLogOut = () => {
    api.delete("/auth/logout").then((response) => {
      if (response.data.statusCode === 200) {
        setAuth({
          id: null,
          role: null
        })
        navigate("/")
      }
    })
  }

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-base-content">All Available Roadmaps</h1>
              <p className="text-base-content/70 mt-1">Explore and track your learning progress</p>
            </div>
            <div className="flex gap-3 flex-wrap justify-center items-center">
              <ThemeChanger />

              <a href="/" className="btn btn-outline btn-info gap-2 ">
                <Home size={18} />
                <span>Home</span>
              </a>
              <button className="btn btn-primary btn-outline normal-case gap-2" onClick={() => navigate("/select-topic")}>
                <Plus size={20} />
                <span>Create a New Roadmap</span>
              </button>
              <button className="btn btn-secondary btn-outline normal-case gap-2" onClick={() => handleLogOut()}>
                <LogOut size={20} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
        {
          loading &&
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <DashboardLoader />
          </div>
        }
        {
          !loading &&
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roadmaps.map((roadmap) => (
              <Cards
                key={roadmap._id}
                createdAt={roadmap.createdAt}
                title={roadmap.topicId.name}
                category={roadmap.subTopicId.name}
                id={roadmap._id}
                handleDelete={() => confirmDelete(roadmap._id)}
              />
            ))}
          </div>
        }

        {!loading && roadmaps.length === 0 && (
          <div className="text-center py-12 bg-base-100 rounded-xl">
            <h3 className="mt-4 text-lg font-medium text-base-content">No roadmaps found</h3>
            <p className="mt-1 text-base-content/70">Get started by creating your first roadmap</p>
            <button className="btn btn-primary mt-6 normal-case gap-2" onClick={() => navigate("/select-topic")}>
              <Plus size={18} />
              <span>Create a New Roadmap</span>
            </button>
          </div>
        )}

        {roadmapToDelete && (
          <div className="fixed inset-0 bg-base-200/50 z-10 flex items-center justify-center">
            <div className="bg-base-100 p-6 rounded shadow-md w-full max-w-md">
              <p className="mb-4 text-lg font-semibold">
                Are you sure you want to delete this roadmap?
              </p>
              <div className="flex justify-end gap-2 ">
                <button className="btn btn-error" onClick={handleDeleteConfirmed}>Delete</button>
                <button className="btn btn-secondary" onClick={() => setRoadmapToDelete(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
